import { action } from "@ember/object"
import Component from "@glimmer/component"
import type { UserSummary } from "@shared/models/user"
import parseEmails from "cc-frontend/utils/parse-emails"
import * as JsSearch from "js-search"
import { debounce, isNil, map } from "lodash-es"
import { cached } from "tracked-toolbox"
import type { PowerSelectMultipleWithCreate } from "../../../types/cc-frontend"

interface Signature {
  Args: {
    users: Array<UserSummary>
    onchange: (any) => void
    oncreate: (term: string, search: any) => void
    hideCreateOption?: boolean
    dropdownClass?: string
    triggerClass?: string
  }
}

export default class UserSelectSearch extends Component<Signature> {
  // even though we're not selecting anything, we need it to prevent an error in powerselect
  selected = []
  isOpen = false
  // lastSearchResult: { term: string | null; results: Array<string> | null } = {
  //   term: null,
  //   results: null,
  // }

  @cached
  get search() {
    if (this.args.users === null) return null
    let search = new JsSearch.Search("id")
    search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy()
    search.addIndex(["attributes", "email"])
    search.addIndex(["attributes", "firstName"])
    search.addIndex(["attributes", "lastName"])
    search.addDocuments(this.args.users)
    return search
  }

  userMatcher(this: PowerSelectMultipleWithCreate, user, term) {
    // `this` refers to the power-select component
    // We cache the results as we're fed each user -- so, we do a query on the first time the
    // query changes. Then, we just see if the doc is in the list of results
    if (isNil(this.lastSearchResult) || this.lastSearchResult.term !== term) {
      this.lastSearchResult = {
        term: term,
        results: map(this.searchIndex.search(term), (user) => user.id),
      }
    }
    return this.lastSearchResult.results?.indexOf(user.id)
  }

  /** Debounced to prevent duplicate calls due to create/blur interaction. */
  private debouncedCreate = debounce(
    (term: string, search: any) => this.args.oncreate(term, search),
    100
  )

  @action onCreate(term: string, search: any) {
    this.debouncedCreate(term, search)
  }

  @action onBlur(event) {
    this.debouncedCreate(event.searchText, null)
  }

  @action hideCreateOption(term) {
    let terms = parseEmails(term)
    if (terms.length > 1) {
      return true
    } else {
      return this.search.search(term).length === 0
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Group::SelectSearch": typeof UserSelectSearch
  }
}
